// OrganizationContext.tsx
// Provides organization context for multi-tenancy support

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { supabase } from "../providers/supabase/supabase";

export interface Organization {
    id: number;
    name: string;
    slug: string;
    logo_url?: string;
    role: "owner" | "admin" | "member";
}

interface OrgContextValue {
    currentOrg: Organization | null;
    currentOrgId: number | null;
    organizations: Organization[];
    switchOrg: (orgId: number) => void;
    isLoading: boolean;
}

const OrganizationContext = createContext<OrgContextValue | null>(null);

// Storage key for persisting current org
const CURRENT_ORG_KEY = "currentOrgId";

// Get current org ID from localStorage
// Get current org ID from localStorage
export function getCurrentOrgId(): number | null {
    const stored = localStorage.getItem(CURRENT_ORG_KEY);
    if (stored) {
        return Number(stored);
    }
    // If not set, return null - do NOT default to 1 as it breaks multi-tenancy for users not in org 1
    return null;
}

// Set current org ID in localStorage
export function setCurrentOrgId(orgId: number): void {
    localStorage.setItem(CURRENT_ORG_KEY, String(orgId));
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
    const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchOrganizations();

        // Listen for auth changes to handle login/logout without page reload
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === "SIGNED_IN") {
                // Clear state effectively (though fetch will overwrite)
                // and fetch new user's orgs
                fetchOrganizations();
            } else if (event === "SIGNED_OUT") {
                setOrganizations([]);
                setCurrentOrg(null);
                // We don't remove localStorage here to avoid race conditions with authProvider 
                // but we reset the in-memory state.
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    async function fetchOrganizations() {
        try {
            // First get the current authenticated user
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                console.log("No authenticated user, using default org");
                setDefaultOrg();
                return;
            }

            // Get user's sales_id from the sales table
            const { data: salesData } = await supabase
                .from("sales")
                .select("id")
                .eq("user_id", user.id)
                .single();

            if (!salesData) {
                console.log("No sales record for user, using default org");
                setDefaultOrg();
                return;
            }

            // Fetch THIS user's organization memberships with org details
            const { data, error } = await supabase
                .from("org_memberships")
                .select(`
                    org_id,
                    role,
                    organizations (id, name, slug, logo_url)
                `)
                .eq("sales_id", salesData.id)
                .eq("is_active", true);

            if (error) {
                console.error("Error fetching organizations:", error);
                setDefaultOrg();
                return;
            }

            if (data && data.length > 0) {
                const orgs: Organization[] = data.map((m: any) => ({
                    id: m.organizations.id,
                    name: m.organizations.name,
                    slug: m.organizations.slug,
                    logo_url: m.organizations.logo_url,
                    role: m.role as "owner" | "admin" | "member",
                }));

                setOrganizations(orgs);

                // Set current org from localStorage, but ONLY if user belongs to it
                const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY);
                let current: Organization | undefined;

                if (savedOrgId) {
                    current = orgs.find((o) => o.id === Number(savedOrgId));
                }

                // If no saved org or user doesn't belong to saved org, use first org
                if (!current) {
                    current = orgs[0];
                }

                if (current) {
                    setCurrentOrg(current);
                    setCurrentOrgId(current.id);
                }
            } else {
                // No memberships found - use default org approach
                setDefaultOrg();
            }
        } catch (err) {
            console.error("Error in fetchOrganizations:", err);
            setDefaultOrg();
        } finally {
            setIsLoading(false);
        }
    }

    async function setDefaultOrg() {
        // Fallback: fetch default org directly
        try {
            const { data } = await supabase
                .from("organizations")
                .select("id, name, slug, logo_url")
                .eq("slug", "default")
                .single();

            if (data) {
                const defaultOrg: Organization = {
                    id: data.id,
                    name: data.name,
                    slug: data.slug,
                    logo_url: data.logo_url,
                    role: "member",
                };
                setOrganizations([defaultOrg]);
                setCurrentOrg(defaultOrg);
                setCurrentOrgId(defaultOrg.id);
            }
        } catch (err) {
            console.error("Error fetching default org:", err);
        }
    }

    function switchOrg(orgId: number) {
        const org = organizations.find((o) => o.id === orgId);
        if (org) {
            setCurrentOrg(org);
            setCurrentOrgId(orgId);
            // Reload to refresh all data with new org context
            window.location.reload();
        }
    }

    return (
        <OrganizationContext.Provider
            value={{
                currentOrg,
                currentOrgId: currentOrg?.id ?? null,
                organizations,
                switchOrg,
                isLoading,
            }}
        >
            {children}
        </OrganizationContext.Provider>
    );
}

export function useOrganization() {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error("useOrganization must be used within an OrganizationProvider");
    }
    return context;
}

// Optional hook that doesn't throw (for use outside provider)
export function useOrganizationOptional() {
    return useContext(OrganizationContext);
}
